import React, { useState, useRef } from 'react';
import { object, number, array } from 'yup';
import { Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';

import useHttpStatus from '~/commons/hooks/useHttpStatus';
import { initFormValues } from '~/commons/helpers';
import env from '~/commons/config/env';

import UnloadPrompt from '~/commons/components/UnloadPrompt.jsx';
import FormHttpSaveBtn from '~/commons/components/FormHttpSaveBtn.jsx';
import { useModalAlert } from '../modals/ModalAlert.jsx';
import useTimer from '../../hooks/useTimer';
import Padding from '../layout/Padding.jsx';
import ModalAnsConfirm from '../modals/ModalAnsConfirm.jsx';
import FormAutoSave from './FormAutoSave.jsx';
import FieldsQuestions from './FieldsQuestions.jsx';

const schema = object().shape({
  answers: array().of(
    object({
      answered: number(),
    }),
  ),
});

const FormAnswer = ({
  answer,
  competition: {
    completedMsg,
    duration, // duration in minutes
    questions,
  },
}) => {
  const history = useHistory();
  const { alert } = useModalAlert();
  const [canLeave, setCanLeave] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const qRef = useRef(null);
  const [submitRes, submitReq] = useHttpStatus();
  const [saveRes, saveReq] = useHttpStatus();
  const defaults = initFormValues({
    answers: questions.map((q) => ({
      question: q._id,
      answered: answer
        ? (answer.answers.find((a) => a.question === q._id) || {}).answered
        : undefined,
    })),
  });
  let { timeLeft, fmtTimeLeft } = useTimer({
    duration: duration * 1000 * 60,
  });

  // if (env.audit) {
  //   timeLeft = duration * 1000 * 60;
  //   fmtTimeLeft = `${duration}:00`;
  // }

  // intermediate save
  const save = async (values) => {
    if (!env.audit) {
      saveReq({
        url: `api/quiz/answers/${answer._id}`,
        method: 'patch',
        data: values,
      });
    }
  };

  // final submit
  const submit = async (values, timesup = false) => {
    if (env.audit) {
      history.push('/app');

      return;
    }

    // make the request
    const { status, payload } = await submitReq({
      url: `api/quiz/answers/${answer._id}`,
      method: 'patch',
      data: { ...values, completed: true },
    });

    if (status === 'success') {
      // completed msg
      alert((
        <div className="text-secondary">
          你的答案已提交
          <br/>
          {completedMsg}
        </div>
      ));
    } else {
      alert((
        <h5 className="text-danger">
          <FA icon={faExclamationCircle} />
          &nbsp;
          {(payload !== '網絡異常' && payload) || '網絡異常，未能提交答案'}
        </h5>
      ), 'danger');
    }

    if (status === 'success' || timesup) {
      // no confirm
      setCanLeave(true);
      // back to main page
      history.push('/app');
    }
  };

  return (
    <>
      <UnloadPrompt
        when={!canLeave}
        message={
          ` 離開當前頁面後無法返回答題頁面，餘下時間還有${fmtTimeLeft}`
        }
      />
      <Formik
        validationSchema={schema}
        initialValues={defaults}
        onSubmit={async () => {
          setConfirmModal(true);
        }}
      >
        {({ values }) => (
          <>
            <FormAutoSave
              onSave={save}
              saveOn={/*env.audit ? null :*/ 30 * 1000}
              onSubmit={(v) => {
                submit(v, true);
              }}
              submitOn={/*env.audit ? null :*/ duration * 1000 * 60}
            />
            <ModalAnsConfirm
              timeLeft={fmtTimeLeft}
              show={confirmModal}
              onConfirm={() => {
                setConfirmModal(false);
                submit(values);
              }}
              onReject={() => {
                setConfirmModal(false);

                const empty = values.answers
                  .find((a) => a.answered === undefined)
                  ?.question;

                if (empty) {
                  qRef.current.scrollIntoView(empty);
                }
              }}
            />
            <Form>
              <div
                className={`
                  toast bg-danger text-white
                  pos-fixed-tc mt-3
                  tra-opacity
                  ${saveRes.status === 'error' ? 'show' : ''}
                `}
                style={{ zIndex: 1000 }}
              >
                <div className="toast-body">
                  <FA icon={faExclamationCircle} />
                  &nbsp;
                  {saveRes.payload || '網絡異常'}
                </div>
              </div>

              {/* header */}
              <Padding className={`pos-sticky ${
                timeLeft <= 3 * 60 * 1000
                  ? 'alert-danger'
                  : 'bg-light text-primary'
              }`}>
                <div className="row align-items-center">
                  <div className="col-md-6 offset-md-3 py-3 text-center">
                    <h4 className="my-1 text-secondary">
                      餘下時間: {fmtTimeLeft}
                    </h4>
                  </div>
                  <div className="col-3 d-none d-md-block text-right">
                    <FormHttpSaveBtn
                      res={submitRes}
                      className="btn-primary px-3 rounded-pill"
                      onlyValid
                    >提交答案</FormHttpSaveBtn>
                  </div>
                </div>
              </Padding>

              {/* questions */}
              <FieldsQuestions
              ref={qRef}
                questions={questions}
              />

              <div className="bg-white pos-sticky-bottom d-md-none">
                <FormHttpSaveBtn
                  res={submitRes}
                  className="btn-primary btn-block py-3"
                  onlyValid
                >提交答案</FormHttpSaveBtn>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};

export default FormAnswer;
